import { mergeAttributes, Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { AiPromptBlockView } from "./ai-prompt-node-view";

export interface AiPromptBlockAttrs {
  instructions_doc: Record<string, unknown>;
  length_sentences: number; // 1..5
  label?: string | null;
}

export interface AiPromptBlockOptions {
  HTMLAttributes: Record<string, unknown>;
  /** Called with the chip's attrs when the user clicks it to edit. */
  onEditNode?: (attrs: AiPromptBlockAttrs) => void;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    aiPromptBlock: {
      insertAiPromptBlock: (attrs: AiPromptBlockAttrs) => ReturnType;
      updateAiPromptBlock: (attrs: AiPromptBlockAttrs) => ReturnType;
    };
  }
}

const MIN_LENGTH = 1;
const MAX_LENGTH = 5;
const DEFAULT_LENGTH = 2;

const EMPTY_INSTRUCTIONS = { type: "doc", content: [] };

function normalizeLength(value: unknown): number {
  if (value === null || value === undefined || value === "") return DEFAULT_LENGTH;
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_LENGTH;
  const truncated = Math.trunc(parsed);
  if (truncated < MIN_LENGTH) return MIN_LENGTH;
  if (truncated > MAX_LENGTH) return MAX_LENGTH;
  return truncated;
}

export const AiPromptBlock = Node.create<AiPromptBlockOptions>({
  name: "ai-prompt-block",

  inline: true,
  group: "inline",
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      onEditNode: undefined,
    };
  },

  addAttributes() {
    return {
      instructions_doc: {
        default: EMPTY_INSTRUCTIONS,
        parseHTML: (el) => {
          const raw = el.getAttribute("data-instructions");
          if (!raw) return { type: "doc", content: [] };
          try {
            return JSON.parse(raw);
          } catch {
            return { type: "doc", content: [] };
          }
        },
        renderHTML: (attrs) => ({
          "data-instructions": JSON.stringify(
            attrs.instructions_doc ?? { type: "doc", content: [] },
          ),
        }),
      },
      length_sentences: {
        default: DEFAULT_LENGTH,
        parseHTML: (el) => normalizeLength(el.getAttribute("data-length-sentences")),
        renderHTML: (attrs) => ({
          "data-length-sentences": String(normalizeLength(attrs.length_sentences)),
        }),
      },
      label: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-label") ?? null,
        renderHTML: (attrs) =>
          typeof attrs.label === "string" && attrs.label.trim()
            ? { "data-label": attrs.label }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'span[data-type="ai-prompt-block"]' },
      { tag: 'div[data-type="ai-prompt-block"]' },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { "data-type": "ai-prompt-block", contenteditable: "false" },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addCommands() {
    return {
      insertAiPromptBlock:
        (attrs: AiPromptBlockAttrs) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContent({
              type: this.name,
              attrs: {
                ...attrs,
                length_sentences: normalizeLength(attrs.length_sentences),
              },
            })
            .run(),
      updateAiPromptBlock:
        (attrs: AiPromptBlockAttrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, {
            ...attrs,
            length_sentences: normalizeLength(attrs.length_sentences),
          }),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(AiPromptBlockView, { as: "span" });
  },
});
